import { Card, Typography } from 'antd';
import * as echarts from 'echarts';
import React, { useEffect, useRef } from 'react';

const { Title } = Typography;

interface AlertPlateProps {
  alertPlateData?: {
    提示?: number;
    次要?: number;
    紧急?: number;
    重要?: number;
  };
}

const colorMap: Record<string, string> = {
  紧急: '#FF8C42', // 橙色
  重要: '#4A90E2', // 蓝色
  次要: '#87CEEB', // 浅蓝色
  提示: '#9370DB', // 紫色
};

const AlertPlateCard: React.FC<AlertPlateProps> = ({ alertPlateData }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    // 如果数据为null，不渲染图表
    if (alertPlateData === null || alertPlateData === undefined) {
      return;
    }

    // 初始化图表
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    // 定义固定的告警级别顺序
    const alertLevels = ['紧急', '重要', '次要', '提示'];
    
    // 处理数据，将对象转换为数组格式，保留所有项包括值为0的
    const data = alertLevels.map((key) => ({
      name: key,
      value: alertPlateData?.[key as keyof typeof alertPlateData] || 0,
      itemStyle: {
        color: colorMap[key] || '#999',
      },
    }));

    // 配置选项 - 使用 ECharts 官方圆角饼图示例样式
    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c}',
      },
      legend: {
        orient: 'vertical',
        left: '70%',
        top: 'center',
        itemGap: 15,
        textStyle: {
          fontSize: 12,
        },
        data: alertLevels,
        formatter: (name: string) => {
          const item = data.find((d) => d.name === name);
          return `${name}: ${item?.value || 0}`;
        },
      },
      series: [
        {
          name: '报警分类',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['32%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
          },
          labelLine: {
            show: false,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          data: data,
        },
      ],
    };

    chartInstance.current.setOption(option);

    // 响应式调整 - 监听窗口大小变化
    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);

    // 使用 ResizeObserver 监听容器大小变化（更精确，可以检测到面板展开/收起等导致的容器大小变化）
    const resizeObserver = new ResizeObserver(() => {
      chartInstance.current?.resize();
    });

    if (chartRef.current) {
      resizeObserver.observe(chartRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [alertPlateData]);

  return (
    <Card
      style={{
        height: '100%',
        width: '100%',
      }}
      title={
        <Title style={{ marginTop: '20px', fontSize: '16px' }} level={3}>
          报警分类
        </Title>
      }
    >
      {alertPlateData === null || alertPlateData === undefined ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999', height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>无数据</div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginTop: '-60px', height: '250px' }}>
            <div ref={chartRef} style={{ width: '100%', height: '100%' }} />
          </div>
          <div style={{ fontSize: 14, color: '#aaa', textAlign: 'center', marginTop: '-10px' }}>
            数据统计截止昨日24时
          </div>
        </>
      )}
    </Card>
  );
};

export default AlertPlateCard;
